import { NgClass, NgStyle } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';

export interface Option {
  value: string;
  label: string;
  code?: string;
  /** Clase de icono opcional (p. ej. "bi bi-gem") que se muestra a la izquierda de la opción. */
  icon?: string;
}

@Component({
  selector: 'app-select-search',
  standalone: true,
  imports: [NgClass, NgStyle],
  templateUrl: './select-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectSearchComponent),
    multi: true,
  }],
})
export class SelectSearchComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  @Input() datalist: Option[] = [];
  @Input() placeholder: string = 'Selecciona una opción';
  @Input() className: string = '';
  @Input() useLabelAsValue = false;
  @Input() behaveAsSelect: boolean = false;
  @Input() withCreateActionButton: boolean = false;
  @Input() actionRoute: string = '';
  @Input() actionButtonIcon: string = 'bi-plus-circle';
  @Input() actionButtonText: string = 'nuevo';
  @Input() autocomplete: string = 'off';
  @Input() dropdownMaxHeight: string = 'max-h-60';
  @Input() disabled: boolean = false;
  @Input() invalid: boolean = false;
  /**
   * Modo flotante (opt-in): renderiza el desplegable con `position: fixed` y coordenadas
   * calculadas desde el input, de modo que se superponga y escape de contenedores con
   * `overflow` (p. ej. el cuerpo scrolleable de un modal) en lugar de quedar recortado.
   * Por defecto `false`: no altera el comportamiento existente (absoluto dentro del flujo).
   */
  @Input() floating: boolean = false;
  @Input()
  set value(val: string) {
    this._value = val;
    this.searchTerm = this.getLabelFromValue(val);
  }
  get value(): string {
    return this._value;
  }
  @Input() defaultValue: Option = { label: '', value: '' };

  @Output() valueChange = new EventEmitter<string>();
  @Output() beforeNavigate = new EventEmitter<string>();

  isOpen = false;
  private _value = '';
  searchTerm = '';
  filteredOptions: Option[] = [];
  randomId: string;

  /** Estilos inline del desplegable cuando floating=true (position fixed + coordenadas). */
  dropdownStyle: { [key: string]: string } = {};

  /** Reposiciona el desplegable flotante ante scroll/resize mientras está abierto. */
  private readonly repositionHandler = (): void => {
    if (this.floating && this.isOpen) {
      this.updateDropdownPosition();
      this.cdr.markForCheck();
    }
  };

  private onChange = (value: string) => { };
  onTouched = () => { };

  constructor(
    private el: ElementRef,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.randomId = this.generateRandomId();
  }

  ngOnInit(): void {
    this.resetFilteredOptions();
    // Solo en modo flotante: escuchar scroll (en captura, para alcanzar scroll de
    // contenedores internos como el cuerpo de un modal) y resize para reposicionar.
    if (this.floating) {
      document.addEventListener('scroll', this.repositionHandler, true);
      window.addEventListener('resize', this.repositionHandler);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.repositionHandler, true);
    window.removeEventListener('resize', this.repositionHandler);
  }

  /**
   * Calcula la posición fija del desplegable a partir del rect del input. Si no hay
   * espacio suficiente debajo, lo despliega hacia arriba (anclado por `bottom`).
   */
  private updateDropdownPosition(): void {
    if (!this.floating) return;
    const input = this.el.nativeElement.querySelector('input') as HTMLElement | null;
    if (!input) return;
    const r = input.getBoundingClientRect();
    const gap = 4;
    const estimatedMaxHeight = 240; // ~max-h-60
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < estimatedMaxHeight && r.top > spaceBelow;
    this.dropdownStyle = openUp
      ? {
          position: 'fixed',
          left: `${r.left}px`,
          width: `${r.width}px`,
          bottom: `${window.innerHeight - r.top + gap}px`
        }
      : {
          position: 'fixed',
          left: `${r.left}px`,
          width: `${r.width}px`,
          top: `${r.bottom + gap}px`
        };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['datalist']) {
      const fullList = this.useLabelAsValue
        ? this.datalist.map(opt => ({ value: opt.label, label: opt.label }))
        : this.datalist;

      if (this.isOpen && this.searchTerm) {
        // Dropdown open with active search: re-filter with current term
        this.filteredOptions = fullList.filter(opt =>
          opt.label.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      } else {
        // Dropdown closed, or open without search: show full list
        this.filteredOptions = fullList;
        if (!this.isOpen) {
          this.searchTerm = this.getLabelFromValue(this._value);
        }
      }

      this.cdr.markForCheck();
    }

    if (changes['value'] && !changes['value'].firstChange) {
      this.searchTerm = this.getLabelFromValue(this._value);
    }

    if (changes['defaultValue']) this.selectOption(this.defaultValue);
  }

  writeValue(value: any): void {
    this._value = value != null ? String(value) : '';
    this.searchTerm = this.getLabelFromValue(this._value);
    this.resetFilteredOptions();
    // ControlValueAccessor recibe valores programáticos (patchValue, setValue) fuera
    // del flujo normal de eventos del DOM. Con OnPush, Angular no detecta estos cambios
    // automáticamente — es necesario marcar el componente para que el template repinte
    // el nuevo searchTerm sin requerir ninguna interacción del usuario.
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    if (this.isOpen) {
      // Closing: restore display text and reset filter
      this.isOpen = false;
      this.searchTerm = this.getLabelFromValue(this._value);
      this.resetFilteredOptions();
    } else {
      // Opening: clear search to show full list, then open
      this.searchTerm = '';
      this.resetFilteredOptions();
      this.isOpen = true;
      if (this.floating) this.updateDropdownPosition();
    }
  }

  onInput(event: any) {
    if (this.behaveAsSelect) return;
    this.searchTerm = event.target.value;
    this.isOpen = true;
    if (this.floating) this.updateDropdownPosition();

    this.filteredOptions = this.datalist.filter(opt =>
      opt.label.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  selectOption(option: Option): void {
    if (!option) return;
    this._value = option.value;
    this.searchTerm = option.label;

    this.onChange(option.value);
    this.valueChange.emit(option.value);

    this.isOpen = false;
    this.resetFilteredOptions();
  }

  clearSelection(): void {
    this._value = '';
    this.searchTerm = '';

    this.onChange('');
    this.valueChange.emit('');

    this.resetFilteredOptions();
    this.isOpen = false;
  }

  /**
   * Restaura filteredOptions al listado completo.
   * Garantiza que al abrir el dropdown o al cambiar el valor programaticamente
   * siempre se muestre la lista completa sin filtros residuales.
   */
  private resetFilteredOptions(): void {
    this.filteredOptions = this.useLabelAsValue
      ? this.datalist.map(opt => ({ value: opt.label, label: opt.label }))
      : this.datalist;
  }

  getLabelFromValue(value: string): string {
    return this.datalist.find(opt => opt.value === value)?.label ?? '';
  }

  isOptionSelected(option: Option): boolean {
    if (this.value == null || option == null) {
      return false;
    }
    return String(this.value).trim() === String(option.value).trim();
  }

  generateRandomId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.searchTerm = this.getLabelFromValue(this._value);
        this.resetFilteredOptions();
      }
    }
  }

  navigateToAction(): void {
    if (this.actionRoute) {
      // Emitir evento antes de navegar para que el componente padre pueda preparar el sidebar
      this.beforeNavigate.emit(this.actionRoute);

      // Solo navegar si NO es una ruta hash (que se usa para acciones custom)
      if (!this.actionRoute.startsWith('#')) {
        this.router.navigate([this.actionRoute], {
          queryParams: { omo: true }
        });
      }
    }
    this.isOpen = false;
    this.searchTerm = this.getLabelFromValue(this._value);
    this.resetFilteredOptions();
  }
}
